/**
 * Contact Support: the form's rules, as pure functions.
 *
 * THE LIMITS ARE MIRRORED FROM `sabha.domain.support_request` and the server
 * re-checks every one of them, by CONTENT rather than by the name or type a
 * browser reports. These exist to spare a person an upload that is certain to
 * be refused and to say why straight away -- they are a courtesy, never the
 * gate, so a drift between the two copies costs a clearer error and nothing
 * worse.
 */

export const SUPPORT_LIMITS = {
  subjectMax: 150,
  descriptionMax: 5_000,
  maxFiles: 3,
  maxFileBytes: 2 * 1024 * 1024,
  maxTotalBytes: 2.5 * 1024 * 1024,
} as const

export const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const
const ACCEPTED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp'] as const

/** Passed to `<input accept>`. Narrows the picker; it does not enforce anything. */
export const ACCEPT_ATTRIBUTE = [
  ...ACCEPTED_TYPES,
  ...ACCEPTED_EXTENSIONS.map((e) => `.${e}`),
].join(',')

export interface FileLike {
  name: string
  size: number
  type: string
}

export type TextProblem = 'required' | 'too_long'

const collapse = (value: string) => value.replace(/\s+/g, ' ').trim()

/** Mirrors the server, which collapses whitespace in a subject before counting. */
export function validateSubject(value: string): TextProblem | null {
  const text = collapse(value)
  if (!text) return 'required'
  return text.length > SUPPORT_LIMITS.subjectMax ? 'too_long' : null
}

export function validateDescription(value: string): TextProblem | null {
  const text = value.trim()
  if (!text) return 'required'
  return text.length > SUPPORT_LIMITS.descriptionMax ? 'too_long' : null
}

export type FileProblem = 'unsupported' | 'empty' | 'too_large' | 'too_many' | 'total_too_large'

export interface RejectedFile {
  name: string
  problem: FileProblem
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase()
}

/**
 * Whether the browser says this is an image we accept. A file with no reported
 * type (drag-and-drop on some platforms) falls back to its extension; a
 * reported type always wins, so `photo.png` labelled `application/pdf` is
 * refused here as it would be on the server.
 */
export function isAcceptedImage(file: FileLike): boolean {
  if (file.type) return (ACCEPTED_TYPES as readonly string[]).includes(file.type)
  return (ACCEPTED_EXTENSIONS as readonly string[]).includes(extensionOf(file.name))
}

export interface FileSelection<T extends FileLike> {
  files: T[]
  rejected: RejectedFile[]
}

/**
 * Add a batch to what is already attached. Every file is judged in order
 * against what has been ACCEPTED so far, so picking four at once keeps the
 * first three and names the fourth, rather than rejecting the lot.
 */
export function addFiles<T extends FileLike>(current: T[], incoming: T[]): FileSelection<T> {
  const files = [...current]
  const rejected: RejectedFile[] = []
  let total = files.reduce((sum, f) => sum + f.size, 0)

  for (const file of incoming) {
    let problem: FileProblem | null = null
    if (!isAcceptedImage(file)) problem = 'unsupported'
    else if (file.size === 0) problem = 'empty'
    else if (file.size > SUPPORT_LIMITS.maxFileBytes) problem = 'too_large'
    else if (files.length >= SUPPORT_LIMITS.maxFiles) problem = 'too_many'
    else if (total + file.size > SUPPORT_LIMITS.maxTotalBytes) problem = 'total_too_large'

    if (problem) {
      rejected.push({ name: file.name, problem })
    } else {
      files.push(file)
      total += file.size
    }
  }
  return { files, rejected }
}

export function removeFile<T extends FileLike>(files: T[], index: number): T[] {
  return files.filter((_, i) => i !== index)
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
